import { createAction, handleActions } from 'redux-actions';
import { createSelector } from 'reselect';
import { useRedux } from 'util/hook/redux';
import { push } from 'react-router-redux';

import { wrapAuthFetch } from 'util/api';

import { openWarningAlert } from './alert';
import { openConfirm } from './confirm';

export const getOrderList = createAction('GET_ORDER_LIST', async () => {
	const { status, data } = await wrapAuthFetch('payment');

	if (status !== 200) {
		return { data: {}, list: [] };
	}

	const ordersData = {};
	const list = data.map(item => {
		ordersData[item.id] = item;

		return item.id;
	});

	return { data: ordersData, list };
});

export const getOrderDetail = createAction('GET_ORDER_DETAIL', async tradeNo => {
	const { status, data } = await wrapAuthFetch(`payment/${tradeNo}`);

	if (status !== 200) {
		return { detail: {} };
	}

	return { detail: data };
});

const cancelOrder = createAction('CANCEL_ORDER', (tradeNo, type) => async (dispatch, getState) => {
	let endpoint;
	let postData;

	const {
		order: { cancelForm },
	} = getState();

	switch (type) {
		case 'atm':
			endpoint = `payment/cancelAtm/${tradeNo}`;
			postData = {
				refund_bank_name: cancelForm.bank,
				refund_bank_branch: cancelForm.branch,
				refund_bank_account: cancelForm.account,
				refund_bank_account_name: cancelForm.name,
			};
			break;
		case 'credit':
			endpoint = `payment/cancelCredit/${tradeNo}`;
			postData = {};
			break;
		case 'linePay':
			endpoint = `payment/cancelCredit/${tradeNo}`;
			postData = {};
			break;
		default:
			endpoint = `payment/cancelUnpaid/${tradeNo}`;
			postData = {};
	}

	const { status } = await wrapAuthFetch(endpoint, {
		method: 'POST',
		body: JSON.stringify(postData),
	});

	if (status !== 200) {
		return { data: {} };
	}

	return dispatch(push('/user/order/cancel/success'));
});

const confirmCancelOrder = createAction('CONFIRM_CANCEL_ORDER', (tradeNo, type) => dispatch =>
	dispatch(
		openConfirm(
			'請問是否要取消此訂單？',
			'請注意：取消後無法更改此訂單',
			cancelOrder(tradeNo, type),
		),
	),
);

const updateCancelForm = createAction('UPDATE_CANCEL_FORM', (key, value) => ({ key, value }));

export const defaultOrderData = {
	id: 1,
	trade_no: '',
	ec_trade_no: '',
	status: '',
	price: 0,
	shipping_fee: 0,
	credit: 0,
	total: 0,
	coupon_discount: 0,
	delivery: {
		name: '',
		price: 0,
		type: '',
	},
	delivery_order_no: '',
	items: [],
	promotions: [],
};

const reducer = {
	order: handleActions(
		{
			GET_ORDER_LIST_PENDING: state => ({
				...state,
				loading: true,
			}),

			GET_ORDER_LIST_FULFILLED: (state, action) => ({
				...state,
				data: action.payload.data,
				list: action.payload.list,
				loading: false,
			}),

			GET_ORDER_DETAIL_PENDING: state => ({
				...state,
				loading: true,
			}),

			GET_ORDER_DETAIL_FULFILLED: (state, action) => ({
				...state,
				detail: action.payload.detail,
				loading: false,
			}),

			UPDATE_CANCEL_FORM: (state, action) => ({
				...state,

				cancelForm: {
					...state.cancelForm,
					[action.payload.key]: action.payload.value,
				},
			}),
		},
		{
			data: {},
			list: [],
			detail: defaultOrderData,
			cancelForm: {
				bank: '',
				branch: '',
				account: '',
				name: '',
			},
			loading: false,
		},
	),
};

const mapHooksToStateByTotal = createSelector(
	state => state.order.list,
	list => list,
);

export const useTotalOrder = () => useRedux(mapHooksToStateByTotal);

const mapHooksToStateByFilter = createSelector(
	(state, status) =>
		state.order.list.filter(li => status.some(s => s === state.order.data[li].status)),
	list => list,
);

export const useFilterOrder = status => useRedux(state => mapHooksToStateByFilter(state, status));

const mapHooksToStateByDetail = createSelector(
	state => state.order.detail,
	detail => detail,
);

export const useOrderDetail = () =>
	useRedux(mapHooksToStateByDetail, { confirmCancelOrder, openWarningAlert, cancelOrder });

const selectCancelForm = createSelector(
	state => state.order.cancelForm,
	form => form,
);

export const useOrderCancelForm = () =>
	useRedux(selectCancelForm, { updateCancelForm, cancelOrder });

const mapHooksToState = createSelector(
	(state, id) => state.order.data[id] || defaultOrderData,
	data => data,
);

export const useOrder = id => useRedux(state => mapHooksToState(state, id), { confirmCancelOrder });

export default { reducer };
